import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth/store';

export function Login() {
  const navigate = useNavigate();
  const setLoggedIn = useAuthStore((state) => state.setLoggedIn);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ passcode }),
      });

      if (res.status === 401) {
        setLoggedIn(false);
        setError('Incorrect passcode');
        return;
      }

      if (!res.ok) {
        setLoggedIn(false);
        setError('Unable to log in right now');
        return;
      }

      setLoggedIn(true);
      navigate('/tenants');
    } catch {
      setLoggedIn(false);
      setError('Unable to log in right now');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen bg-zinc-950">
      {/* Left panel */}
      <div className="hidden w-1/2 flex-col justify-between bg-zinc-900 p-12 lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-500">
            <svg viewBox="0 0 20 20" fill="white" className="h-4 w-4">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-white">Email Proto</span>
        </div>
        <div>
          <p className="text-2xl font-semibold leading-snug text-white">
            Multi-tenant outbound email — managed from one place.
          </p>
          <p className="mt-3 text-sm text-zinc-500">
            Create sender identities, test delivery, and inspect SES reputation events across all tenants.
          </p>
        </div>
        <p className="text-xs text-zinc-700">might.solutions</p>
      </div>

      {/* Right panel */}
      <div className="flex flex-1 items-center justify-center bg-zinc-50 px-8">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Sign in</h1>
            <p className="mt-1.5 text-sm text-zinc-500">Enter your admin passcode to continue.</p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-zinc-700" htmlFor="passcode">
                Passcode
              </label>
              <input
                id="passcode"
                type="password"
                value={passcode}
                onChange={(event) => setPasscode(event.target.value)}
                className="w-full rounded-lg border border-zinc-300 bg-white px-3.5 py-2.5 text-sm text-zinc-900 shadow-sm placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                autoComplete="current-password"
                autoFocus
              />
            </div>

            {error ? (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3.5 py-2.5 text-sm text-red-700 ring-1 ring-inset ring-red-200">
                <svg viewBox="0 0 16 16" fill="currentColor" className="h-4 w-4 shrink-0">
                  <path fillRule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zm0-4a.75.75 0 01-.75-.75v-4a.75.75 0 011.5 0v4A.75.75 0 018 11zm-.75 2.25a.75.75 0 111.5 0 .75.75 0 01-1.5 0z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
            >
              {submitting ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
